"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const lucide_react_1 = require("lucide-react");
const DebugModal = ({ isOpen, onClose }) => {
    const [debugInfo, setDebugInfo] = (0, react_1.useState)(null);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const collectDebugInfo = async () => {
        setLoading(true);
        const info = {
            timestamp: new Date().toISOString(),
            environment: {
                userAgent: navigator.userAgent,
                url: window.location.href,
                appVersion: process.env.APP_VERSION || 'unknown',
                nodeEnv: process.env.NODE_ENV || 'unknown',
                logoUrl: process.env.NSZM_LOGO_URL || 'not-set',
            },
            api: {
                backendUrl: window.location.origin.replace(':3000', ':8001'),
                projectsStatus: 'loading',
            },
            browser: {
                localStorage: {},
                sessionStorage: {},
                cookies: document.cookie || 'none',
                serviceWorker: 'not-supported',
            },
            network: {
                online: navigator.onLine,
                connection: navigator.connection || null,
            },
            backend: {
                debug_mode: false,
                environment: {},
                system: {},
                services: {}
            },
        };
        // Zkus načíst localStorage/sessionStorage
        try {
            info.browser.localStorage = { ...localStorage };
            info.browser.sessionStorage = { ...sessionStorage };
        }
        catch (e) {
            info.browser.localStorage = { error: 'Access denied' };
            info.browser.sessionStorage = { error: 'Access denied' };
        }
        // Zkontroluj Service Worker
        if ('serviceWorker' in navigator) {
            try {
                const registration = await navigator.serviceWorker.getRegistration();
                if (registration) {
                    info.browser.serviceWorker = 'registered';
                }
                else {
                    info.browser.serviceWorker = 'supported';
                }
            }
            catch (error) {
                info.browser.serviceWorker = 'failed';
                info.browser.swError = error instanceof Error ? error.message : 'Unknown error';
            }
        }
        // Zkus API call na projekty
        try {
            const response = await fetch('/api/v1/projects/', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            if (response.ok) {
                const data = await response.json();
                info.api.projectsStatus = 'success';
                info.api.projectsData = data;
            }
            else {
                info.api.projectsStatus = 'error';
                info.api.projectsError = `HTTP ${response.status}: ${response.statusText}`;
            }
        }
        catch (error) {
            info.api.projectsStatus = 'error';
            info.api.projectsError = error instanceof Error ? error.message : 'Network error';
        }
        // Zkus získat backend debug info
        try {
            const debugResponse = await fetch('/api/v1/admin/debug-info');
            if (debugResponse.ok) {
                const debugData = await debugResponse.json();
                info.backend = {
                    debug_mode: debugData.debug_mode,
                    environment: debugData.environment,
                    system: debugData.system,
                    services: debugData.services
                };
            }
        }
        catch (error) {
            info.backend = {
                debug_mode: false,
                environment: { error: 'Failed to fetch' },
                system: { error: 'Failed to fetch' },
                services: { error: 'Failed to fetch' }
            };
        }
        setDebugInfo(info);
        setLoading(false);
    };
    (0, react_1.useEffect)(() => {
        if (isOpen && !debugInfo) {
            collectDebugInfo();
        }
    }, [isOpen]);
    const copyToClipboard = () => {
        if (debugInfo) {
            navigator.clipboard.writeText(JSON.stringify(debugInfo, null, 2));
        }
    };
    const getStatusIcon = (status) => {
        switch (status) {
            case 'success':
            case 'registered':
                return (0, jsx_runtime_1.jsx)(lucide_react_1.CheckCircle, { className: "w-4 h-4 text-green-600" });
            case 'error':
            case 'failed':
                return (0, jsx_runtime_1.jsx)(lucide_react_1.XCircle, { className: "w-4 h-4 text-red-600" });
            case 'loading':
                return (0, jsx_runtime_1.jsx)(lucide_react_1.RefreshCw, { className: "w-4 h-4 text-blue-600 animate-spin" });
            default:
                return (0, jsx_runtime_1.jsx)(lucide_react_1.AlertTriangle, { className: "w-4 h-4 text-yellow-600" });
        }
    };
    if (!isOpen)
        return null;
    return ((0, jsx_runtime_1.jsx)("div", { className: "fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]", children: (0, jsx_runtime_1.jsxs)("div", { className: "bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden", children: [(0, jsx_runtime_1.jsxs)("div", { className: "flex items-center justify-between p-4 border-b border-gray-200", children: [(0, jsx_runtime_1.jsxs)("h2", { className: "text-lg font-semibold text-gray-900 flex items-center", children: [(0, jsx_runtime_1.jsx)(lucide_react_1.Bug, { className: "w-5 h-5 mr-2 text-blue-600" }), "Diagnostika syst\u00E9mu"] }), (0, jsx_runtime_1.jsxs)("div", { className: "flex items-center space-x-2", children: [(0, jsx_runtime_1.jsx)("button", { onClick: copyToClipboard, className: "p-2 text-gray-400 hover:text-gray-600 transition-colors", title: "Kop\u00EDrovat do schr\u00E1nky", children: (0, jsx_runtime_1.jsx)(lucide_react_1.Copy, { className: "w-4 h-4" }) }), (0, jsx_runtime_1.jsx)("button", { onClick: () => {
                                        setDebugInfo(null);
                                        collectDebugInfo();
                                    }, className: "p-2 text-gray-400 hover:text-gray-600 transition-colors", title: "Obnovit", children: (0, jsx_runtime_1.jsx)(lucide_react_1.RefreshCw, { className: "w-4 h-4" }) }), (0, jsx_runtime_1.jsx)("button", { onClick: onClose, className: "p-1 text-gray-400 hover:text-gray-600 transition-colors", "aria-label": "Zav\u0159\u00EDt", children: (0, jsx_runtime_1.jsx)(lucide_react_1.X, { className: "w-5 h-5" }) })] })] }), (0, jsx_runtime_1.jsx)("div", { className: "p-4 overflow-y-auto max-h-[calc(90vh-120px)]", children: loading ? ((0, jsx_runtime_1.jsxs)("div", { className: "flex items-center justify-center py-8", children: [(0, jsx_runtime_1.jsx)(lucide_react_1.RefreshCw, { className: "w-6 h-6 animate-spin text-blue-600 mr-2" }), (0, jsx_runtime_1.jsx)("span", { children: "Sb\u00EDr\u00E1m diagnostick\u00E9 informace..." })] })) : debugInfo ? ((0, jsx_runtime_1.jsxs)("div", { className: "space-y-6", children: [(0, jsx_runtime_1.jsxs)("div", { className: "grid grid-cols-1 md:grid-cols-3 gap-4", children: [(0, jsx_runtime_1.jsxs)("div", { className: "bg-gray-50 p-3 rounded", children: [(0, jsx_runtime_1.jsxs)("div", { className: "flex items-center", children: [getStatusIcon(debugInfo.api.projectsStatus), (0, jsx_runtime_1.jsx)("span", { className: "ml-2 font-medium", children: "API Status" })] }), (0, jsx_runtime_1.jsx)("div", { className: "text-sm text-gray-600 mt-1", children: debugInfo.api.projectsStatus === 'success' ? 'Funkční' :
                                                    debugInfo.api.projectsStatus === 'error' ? 'Chyba' : 'Načítá...' })] }), (0, jsx_runtime_1.jsxs)("div", { className: "bg-gray-50 p-3 rounded", children: [(0, jsx_runtime_1.jsxs)("div", { className: "flex items-center", children: [getStatusIcon(debugInfo.browser.serviceWorker), (0, jsx_runtime_1.jsx)("span", { className: "ml-2 font-medium", children: "Service Worker" })] }), (0, jsx_runtime_1.jsx)("div", { className: "text-sm text-gray-600 mt-1", children: debugInfo.browser.serviceWorker })] }), (0, jsx_runtime_1.jsxs)("div", { className: "bg-gray-50 p-3 rounded", children: [(0, jsx_runtime_1.jsxs)("div", { className: "flex items-center", children: [getStatusIcon(debugInfo.network.online ? 'success' : 'error'), (0, jsx_runtime_1.jsx)("span", { className: "ml-2 font-medium", children: "S\u00ED\u0165" })] }), (0, jsx_runtime_1.jsx)("div", { className: "text-sm text-gray-600 mt-1", children: debugInfo.network.online ? 'Online' : 'Offline' })] }), (0, jsx_runtime_1.jsxs)("div", { className: "bg-gray-50 p-3 rounded", children: [(0, jsx_runtime_1.jsxs)("div", { className: "flex items-center", children: [getStatusIcon(debugInfo.backend.debug_mode ? 'success' : 'error'), (0, jsx_runtime_1.jsx)("span", { className: "ml-2 font-medium", children: "Debug re\u017Eim" })] }), (0, jsx_runtime_1.jsx)("div", { className: "text-sm text-gray-600 mt-1", children: debugInfo.backend.debug_mode ? 'Zapnut' : 'Vypnut' })] })] }), (0, jsx_runtime_1.jsxs)("div", { className: "bg-gray-50 p-4 rounded-lg", children: [(0, jsx_runtime_1.jsx)("h3", { className: "font-medium mb-2", children: "Kompletn\u00ED diagnostika (JSON)" }), (0, jsx_runtime_1.jsx)("pre", { className: "text-xs bg-white p-3 rounded border overflow-x-auto", children: JSON.stringify(debugInfo, null, 2) })] })] })) : ((0, jsx_runtime_1.jsxs)("div", { className: "text-center py-8", children: [(0, jsx_runtime_1.jsx)(lucide_react_1.Bug, { className: "w-12 h-12 text-gray-400 mx-auto mb-4" }), (0, jsx_runtime_1.jsx)("p", { className: "text-gray-600", children: "Klikn\u011Bte na obnovit pro spu\u0161t\u011Bn\u00ED diagnostiky" })] })) })] }) }));
};
exports.default = DebugModal;
